import { useEffect, useRef, useState } from 'react'
import TitleHeader from '../TitleHeader/TitleHeader'
import arrowicon from '../../../assets/images/icons/arrowicon.svg'
import Constants from '../../../app/utils/constants'

const backendCards = Constants.backendCards

const backendLinks: Record<string, string> = {
  NodeJS: 'https://nodejs.org',
  Redis: 'https://redis.io',
  MongoDB: 'https://www.mongodb.com',
  MySQL: 'https://www.mysql.com',
  Golang: 'https://go.dev',
  Firebase: 'https://firebase.google.com',
}

const BackendService = () => {
  // ── Cursor glow (scroll-aware) ────────────────────────────────────────────
  const containerRef = useRef<HTMLElement>(null)
  const blobRef = useRef<HTMLDivElement>(null)
  const [isInside, setIsInside] = useState(false)

  useEffect(() => {
    let clientX = 0, clientY = 0, currentX = 0, currentY = 0, rafId = 0
    let inside = false

    const container = containerRef.current
    if (!container) return

    const getRelativePos = () => {
      const rect = container.getBoundingClientRect()
      return { x: clientX - rect.left, y: clientY - rect.top }
    }

    const setPos = (x: number, y: number) => {
      if (blobRef.current)
        blobRef.current.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`
    }

    const handleMouseMove = (e: MouseEvent) => {
      clientX = e.clientX
      clientY = e.clientY
    }

    const handleEnter = (e: MouseEvent) => {
      clientX = e.clientX
      clientY = e.clientY
      const { x, y } = getRelativePos()
      currentX = x; currentY = y
      setPos(x, y)
      inside = true
      setIsInside(true)
    }

    const handleLeave = () => {
      inside = false
      setIsInside(false)
    }

    const handleScroll = () => {
      if (!inside) return
      const { x, y } = getRelativePos()
      currentX = x; currentY = y
      setPos(x, y)
    }

    const animate = () => {
      if (inside) {
        const { x, y } = getRelativePos()
        currentX += (x - currentX) * 0.1
        currentY += (y - currentY) * 0.1
        setPos(currentX, currentY)
      }
      rafId = requestAnimationFrame(animate)
    }

    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseenter', handleEnter)
    container.addEventListener('mouseleave', handleLeave)
    window.addEventListener('scroll', handleScroll, { passive: true })
    rafId = requestAnimationFrame(animate)

    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseenter', handleEnter)
      container.removeEventListener('mouseleave', handleLeave)
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(rafId)
    }
  }, [])

  // ── Scroll animation per card ─────────────────────────────────────────────
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const [visibleCards, setVisibleCards] = useState<boolean[]>([])

  useEffect(() => {
    setVisibleCards(new Array(backendCards.length).fill(false))
    const observers: IntersectionObserver[] = []
    cardRefs.current.forEach((el, index) => {
      if (!el) return
      const observer = new IntersectionObserver(
        ([entry]) => {
          setVisibleCards(prev => {
            const next = [...prev]
            next[index] = entry.isIntersecting
            return next
          })
        },
        { threshold: 0.3 }
      )
      observer.observe(el)
      observers.push(observer)
    })
    return () => observers.forEach(o => o.disconnect())
  }, [])

  return (
    <section
      ref={containerRef}
      className="relative w-full overflow-hidden py-12 px-5 md:px-[10%] md:py-16"
    >
      {/* Cursor glow blob */}
      <div
        ref={blobRef}
        className={`pointer-events-none absolute w-[600px] h-[600px] rounded-full
          bg-[radial-gradient(circle_at_center,#9e5ddfba,transparent_70%)]
          blur-3xl z-0 transition-opacity duration-300 ease-out
          ${isInside ? 'opacity-50' : 'opacity-0'}`}
      />

      <style>{`
        .card-hidden {
          transform: translateY(80px);
          opacity: 0;
          transition: all 0.7s ease;
        }
        .card-visible {
          transform: translateY(0);
          opacity: 1;
          transition: all 0.7s ease;
        }
        .float-icon {
          animation: floatY 3s ease-in-out infinite;
        }
        @keyframes floatY {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
          100% { transform: translateY(0px); }
        }
        .arrow-icon {
          transition: opacity 0.3s ease-in;
        }
        .group:hover .arrow-icon {
          opacity: 1;
          animation: arrowMove 1.2s ease-in infinite;
        }
        @keyframes arrowMove {
          0% { transform: translateX(0); }
          50% { transform: translateX(8px); }
          100% { transform: translateX(0); }
        }
        .card-hover:hover {
          transform: scale(1.04);
        }
        .card-hover:hover .circle-scale {
          transform: scale(1.1);
        }
      `}</style>

      {/* Header */}
      <div className="relative z-10 mb-10 flex justify-center md:mb-15">
        <TitleHeader
          badgeText="Software we are using"
          titleLines={['Our Backend Services']}
          wrapperClassName="flex flex-col items-center text-center mb-5 md:mb-0"
          titleClassName="font-rajdhani text-3xl font-semibold md:text-4xl lg:text-4xl xl:text-4xl 2xl:text-6xl"
          badgeClassName="px-8  py-2 text-sm font-rajdhani font-semibold uppercase md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
        />
      </div>

      {/* Cards */}
      <div className="relative z-10 grid grid-cols-2 gap-x-6 2xl:gap-x-25 gap-y-25 sm:grid-cols-3 lg:grid-cols-3">
        {backendCards.map((card, index) => (
          <article
            key={card.title}
            ref={(el: HTMLDivElement | null) => { cardRefs.current[index] = el }}
            onClick={() => {
              const url = backendLinks[card.title]
              if (url) window.open(url, '_blank', 'noopener,noreferrer')
            }}
            className={`group card-hover cursor-pointer relative overflow-visible bg-[#140D34] text-center
              shadow-[0_12px_30px_rgba(0,0,0,0.28)] py-3 sm:py-3 md:py-4 lg:py-5 xl:py-7 2xl:py-10 rounded-[20px]
              ${visibleCards[index] ? 'card-visible' : 'card-hidden'}`}
          >
            <div
              className={`pointer-events-none absolute inset-0 transition-opacity duration-300 rounded-[20px] ${
                index % 2 === 0
                  ? 'bg-[#140D34] group-hover:bg-purple/95'
                  : 'bg-[linear-gradient(92.21deg,#EA892A_0%,#F0578B_100%)] opacity-10 group-hover:opacity-95'
              }`}
            />

            <img
              src={arrowicon}
              alt=""
              className="arrow-icon pointer-events-none absolute right-4 top-4 h-8 w-8 object-contain opacity-0 brightness-0 invert"
            />

            <div
              className={`circle-scale absolute left-1/2 top-0 z-20 h-16 w-16 sm:h-16 sm:w-16 md:h-18 md:w-18 lg:h-18 lg:w-18 xl:h-20 xl:w-20 2xl:h-25 2xl:w-25
                -translate-x-1/2 -translate-y-1/2 rounded-full p-px transition-transform duration-300
                ${index % 2 === 0 ? 'bg-purple' : 'bg-[linear-gradient(135deg,#EA892A_0%,#F0578B_100%)]'}`}
            >
              <div className="flex h-full w-full items-center justify-center rounded-full bg-[#140D34]">
                <img
                  src={card.icon}
                  alt=""
                  className="float-icon h-10 w-10 sm:h-10 sm:w-10 md:h-12 md:w-12 lg:h-10 lg:w-10 xl:h-12 xl:w-12 2xl:h-14 2xl:w-14 object-contain"
                />
              </div>
            </div>

            <h3 className="relative mt-10 z-10 font-gilroy-medium text-xl text-white md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl">
              {card.title}
            </h3>
          </article>
        ))}
      </div>
    </section>
  )
}

export default BackendService